import IProduct, { ILocalProduct } from '../models/IProduct';
import { fromCents, toCents } from './cart-price';

/**
 * Cheapest crust plus cheapest edge of a pizza size, which the card adds to the price it shows
 * because the visitor cannot buy the size without picking one of each.
 */
const getMinPizzaRequiredComplements = (product: IProduct): number => {
  const { pizzaSize } = product as ILocalProduct;

  if (!pizzaSize) return 0;

  const minCrust =
    pizzaSize.pizzaCrusts?.length > 0
      ? Math.min(...pizzaSize.pizzaCrusts.map((crust) => crust?.price || 0))
      : 0;

  const minEdge =
    pizzaSize.pizzaEdges?.length > 0
      ? Math.min(...pizzaSize.pizzaEdges.map((edge) => edge?.price || 0))
      : 0;

  return minCrust + minEdge;
};

/**
 * The price the menu actually shows for a product, before it is configured.
 *
 * The catalog carries two pairs: `unitPrice`/`salePrice`, charged once the item is in the cart, and
 * `suggestedPrice`/`suggestedSalePrice`, which is what the card and the list display. Analytics has
 * to report the second one, otherwise `view_item_list` and `view_item` would disagree with the page
 * the visitor is looking at. The last two fallbacks only ever matter for a product with no
 * suggested price at all.
 */
export const getProductDisplayPrice = (product: IProduct): number => {
  const base =
    product.suggestedSalePrice ||
    product.suggestedPrice ||
    product.salePrice ||
    product.unitPrice ||
    0;

  return fromCents(toCents(base) + toCents(getMinPizzaRequiredComplements(product)));
};

/** How much the promotion takes off the displayed price, or nothing when there is no promotion. */
export const getProductDisplayDiscount = (product: IProduct): number | undefined => {
  if (!product.suggestedSalePrice || !product.suggestedPrice) return undefined;

  const discount = toCents(product.suggestedPrice) - toCents(product.suggestedSalePrice);

  return discount > 0 ? fromCents(discount) : undefined;
};

/**
 * Picks the price table the current channel is supposed to charge and display.
 *
 * The catalog ships both tables on every product and leaves the choice to the client. At a table, a
 * product that opted into the table price table is displayed and charged by its `onTable*` pair.
 *
 * The promotional pair never falls back across channels: a table with no promotion configured has
 * to read as "no promotion" instead of leaking the delivery promotion into the room, which is also
 * what the server charges in `OrderRepository.CalculateProductStandardPrice`. The base pair keeps
 * its long-standing fallback to the delivery value on purpose — the server books 0 there instead,
 * and reproducing that would let a product saved with `onTableUnitPrice = 0` reach the venue API
 * for free.
 */
export const normalizeProductChannelPrices = <T extends IProduct>(
  product: T,
  table: boolean,
): T => {
  if (!table || !product?.onTablePriceEnabled) return product;

  return {
    ...product,
    unitPrice: product.onTableUnitPrice || product.unitPrice,
    salePrice: product.onTableSalePrice,
    suggestedPrice: product.onTableSuggestedPrice || product.suggestedPrice,
    suggestedSalePrice: product.onTableSuggestedSalePrice,
  };
};
